import { memo, useEffect, useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

import { ThreadListItem } from "@/components/messages/thread-list-item";
import { THREAD_CHUNK_SIZE } from "@/lib/messages/messages-constants";
import { getThreadRowId } from "@/lib/messages/messages-helpers";
import { getAuthUser } from "@/lib/api/token-store";
import { appDayDiff, appDayKey, withAppTimeZone } from "@/lib/timezone";

// Rows fetched beyond the visible window in each direction — larger than the render overscan so
// a normal scroll never catches up with unloaded rows.
const FETCH_OVERSCAN_ROWS = 25;
// Trailing settle before fetching the range a scroll landed on (absorbs scrollbar drags).
const RANGE_SETTLE_MS = 100;
// Sparse row count rendered as skeletons while the total is still unknown (cold start).
const UNKNOWN_TOTAL_PLACEHOLDER_ROWS = 12;

/** Civil-day bucket key of a row's last message; "earlier" for rows without a timestamp. */
function dayKeyOf(thread) {
  const raw = thread?.lastMessageAt;
  const t = raw ? new Date(raw).getTime() : NaN;
  if (Number.isNaN(t) || !raw) return "earlier";
  return `day:${appDayKey(new Date(t))}`;
}

/** Same labels as groupThreadsByDay: "Today" / "Yesterday" / weekday, plus "12 Jun". */
function dayLabelsOf(thread, nowMs) {
  const raw = thread?.lastMessageAt;
  const t = raw ? new Date(raw).getTime() : NaN;
  if (Number.isNaN(t) || !raw) return { label: "Earlier", dateLabel: "" };
  const date = new Date(t);
  const dateLabel = date.toLocaleDateString(undefined, withAppTimeZone({ day: "numeric", month: "short" }));
  const dayDiff = appDayDiff(new Date(nowMs), date);
  if (dayDiff <= 0) return { label: "Today", dateLabel };
  if (dayDiff === 1) return { label: "Yesterday", dateLabel };
  return { label: date.toLocaleDateString(undefined, withAppTimeZone({ weekday: "long" })), dateLabel };
}

/**
 * The day separator, rendered INSIDE the first row of each day rather than as its own virtual
 * item: with unloaded regions the number of separators between two offsets is unknowable, and
 * separator items inserted as chunks load would shift every row under the user's scroll position.
 * Same visual as the dense list's sticky day header.
 */
function DayDividerBand({ label, dateLabel }) {
  return (
    <div className="flex items-center gap-2 border-b border-border/40 bg-background/95 px-3 py-1.5 backdrop-blur dark:bg-background/90">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="h-px min-w-4 flex-1 bg-border/70" />
      {dateLabel ? (
        <span className="font-mono text-[10px] text-muted-foreground/80">{dateLabel}</span>
      ) : null}
    </div>
  );
}

/**
 * Placeholder for a not-yet-fetched offset. Fixed at the row estimate so the scrollbar stays
 * stable; NO data-thread-item (the entrance animation must only ever touch real rows).
 */
function ThreadRowSkeleton({ isEven }) {
  return (
    <div
      className={`flex h-[72px] w-full items-center gap-2.5 px-3 py-2 ${isEven ? "bg-muted/20" : ""}`}
      aria-hidden
    >
      <div className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-muted" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
        <div className="h-3 w-2/3 animate-pulse rounded bg-muted/70" />
      </div>
    </div>
  );
}

/**
 * The fully-virtual Messages thread list: sized by the server's exact `total`, rendering
 * skeletons for unloaded offsets and asking the sparse window to fetch whatever the viewport
 * needs. Replaces the dense chronological path of SegmentedThreadList for the normal scope
 * (search / needs lens / internal Chats keep the dense list — their data is complete).
 *
 * `leadingItems` render ABOVE the sparse region (the Groups view's server-complete group
 * sections): dense {type:"header"|"row", key, node|thread, extra} items, same shape as the
 * dense list's flattened items. They sit at the front so chunk loads never shift them.
 */
export const VirtualThreadList = memo(function VirtualThreadList({
  window: threadWindow,
  leadingItems = null,
  // Groups view: the "All threads" section is collapsed — leading items render, the sparse
  // region does not.
  tailCollapsed = false,
  listClockMs,
  threadMemberIdsByThreadId,
  selectedThreadId,
  localUnreadByThreadId,
  onSelectThread,
  onThreadFlagChange,
  groups = [],
  onThreadGroupChange,
  onPatientTagToggle,
  onStatusChange,
  onDeleteThread,
  showWhatsappNumber = false,
  scrollElementRef = null,
}) {
  const leading = Array.isArray(leadingItems) ? leadingItems : [];
  const leadingCount = leading.length;
  const total = threadWindow.total;
  const sparseCount = tailCollapsed ? 0 : total == null ? UNKNOWN_TOTAL_PLACEHOLDER_ROWS : total;
  const count = leadingCount + sparseCount;
  const generation = threadWindow.generation;
  // Thread ids already rendered by the leading (grouped) region. During a stale-while-revalidate
  // beat a just-grouped thread can still sit in a stale sparse chunk — suppress that copy (its
  // slot shows a skeleton and heals on the chunk refetch) so a thread never renders twice.
  const leadingThreadIds = useMemo(() => {
    if (!leading.length) return null;
    const ids = new Set();
    for (const item of leading) {
      if (item?.type === "row") {
        const tid = getThreadRowId(item.thread);
        if (tid) ids.add(tid);
      }
    }
    return ids.size ? ids : null;
  }, [leading]);

  // Resolved once per list render (not once per row) and handed to every row via rowProps.
  const currentUserId = String(getAuthUser()?.id || "").trim();
  const rowProps = {
    listClockMs,
    lens: "all",
    selectedThreadId,
    localUnreadByThreadId,
    currentUserId,
    onSelectThread,
    onThreadFlagChange,
    groups,
    onThreadGroupChange,
    onPatientTagToggle,
    onStatusChange,
    onDeleteThread,
    showWhatsappNumber,
  };

  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => (scrollElementRef ? scrollElementRef.current : null),
    estimateSize: (i) => {
      if (i < leadingCount) return leading[i]?.type === "header" ? 34 : 72;
      return 72;
    },
    overscan: 12,
    getItemKey: (i) => {
      if (i < leadingCount) return leading[i]?.key ?? `lead:${i}`;
      const row = threadWindow.getRow(i - leadingCount);
      const tid = row ? getThreadRowId(row) : "";
      // A leading-region duplicate renders as a skeleton, so it must not claim the row key.
      if (tid && !(leadingThreadIds && leadingThreadIds.has(tid))) return `row:${tid}`;
      return `s:${generation}:${i - leadingCount}`;
    },
  });

  const virtualItems = virtualizer.getVirtualItems();
  const firstIndex = virtualItems.length ? virtualItems[0].index : 0;
  const lastIndex = virtualItems.length ? virtualItems[virtualItems.length - 1].index : 0;
  // Sparse-region bounds of the visible window (fetch overscan applied), clamped to the region.
  const sparseStart = Math.max(0, firstIndex - leadingCount - FETCH_OVERSCAN_ROWS);
  const sparseEnd = Math.max(0, lastIndex - leadingCount + FETCH_OVERSCAN_ROWS);
  const anySparseVisible = lastIndex >= leadingCount && sparseCount > 0;

  const requestRange = threadWindow.requestRange;
  const getRowRef = useRef(threadWindow.getRow);
  getRowRef.current = threadWindow.getRow;
  useEffect(() => {
    if (!anySparseVisible) return undefined;
    // Cold jump (nothing loaded where the user landed): fetch immediately — the settle delay
    // would just prolong the skeletons. Otherwise wait for the scroll to settle.
    let nothingLoaded = true;
    for (let i = sparseStart; i <= sparseEnd; i++) {
      if (getRowRef.current(i)) {
        nothingLoaded = false;
        break;
      }
    }
    if (nothingLoaded) {
      requestRange(sparseStart, sparseEnd);
      return undefined;
    }
    const timer = setTimeout(() => requestRange(sparseStart, sparseEnd), RANGE_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [sparseStart, sparseEnd, anySparseVisible, generation, total, requestRange]);

  // Floating day chip: the day of the topmost VISIBLE loaded row. Replaces the dense list's
  // sticky day headers (impossible here — see DayDividerBand). Hidden near the very top, where
  // the first day's own divider is in view, and while the top row is an unloaded skeleton.
  const chip = useMemo(() => {
    if (!anySparseVisible || virtualizer.scrollOffset < 40) return null;
    for (const vi of virtualItems) {
      // getVirtualItems() includes up to `overscan` rows mounted ABOVE the viewport — skip
      // them, or near every day boundary the chip would label an off-screen row's day.
      if (vi.end <= virtualizer.scrollOffset) continue;
      if (vi.index < leadingCount) continue;
      const row = threadWindow.getRow(vi.index - leadingCount);
      if (row) return dayLabelsOf(row, listClockMs ?? Date.now());
      return null;
    }
    return null;
    // virtualItems identity changes on scroll, which is exactly when the chip must recompute.
  }, [anySparseVisible, virtualItems, leadingCount, threadWindow, listClockMs, virtualizer.scrollOffset]);

  if (count === 0) return null;

  return (
    <>
      {chip ? (
        <div style={{ position: "sticky", top: 6, height: 0, zIndex: 12 }} className="pointer-events-none">
          <div className="mx-auto w-fit rounded-full border border-border/50 bg-background/95 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground shadow-sm backdrop-blur dark:bg-background/90">
            {chip.label}
            {chip.dateLabel ? <span className="ml-1.5 font-mono font-normal opacity-80">{chip.dateLabel}</span> : null}
          </div>
        </div>
      ) : null}
      <div style={{ position: "relative", width: "100%", height: `${virtualizer.getTotalSize()}px` }}>
        {virtualItems.map((virtualRow) => {
          const index = virtualRow.index;
          const style = {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            transform: `translateY(${virtualRow.start}px)`,
          };
          if (index < leadingCount) {
            const item = leading[index];
            if (!item) return null;
            return (
              <div
                key={virtualRow.key}
                data-index={index}
                {...(item.type === "row" ? { "data-thread-item": "" } : {})}
                ref={virtualizer.measureElement}
                style={{ ...style, zIndex: item.type === "header" ? 10 : undefined }}
              >
                {item.type === "header" ? (
                  item.node
                ) : (
                  <ThreadListItem
                    thread={item.thread}
                    index={index}
                    threadMembership={threadMemberIdsByThreadId[getThreadRowId(item.thread)]}
                    {...rowProps}
                    {...item.extra}
                  />
                )}
              </div>
            );
          }
          const sparseIndex = index - leadingCount;
          const rawRow = threadWindow.getRow(sparseIndex);
          // A stale sparse copy of a thread the leading (grouped) region already renders shows
          // as a skeleton until the chunk refetch re-addresses the slot.
          const row =
            rawRow && leadingThreadIds && leadingThreadIds.has(getThreadRowId(rawRow)) ? null : rawRow;
          if (!row) {
            return (
              <div key={virtualRow.key} data-index={index} ref={virtualizer.measureElement} style={style}>
                <ThreadRowSkeleton isEven={sparseIndex % 2 === 0} />
              </div>
            );
          }
          // Divider when this row starts a new civil day — knowable only when the previous offset
          // is loaded (it pops in when a gap fills; measureElement heals the offsets).
          const prevRow = sparseIndex > 0 ? threadWindow.getRow(sparseIndex - 1) : null;
          const showDivider =
            sparseIndex === 0 || (prevRow != null && dayKeyOf(prevRow) !== dayKeyOf(row));
          const labels = showDivider ? dayLabelsOf(row, listClockMs ?? Date.now()) : null;
          return (
            <div
              key={virtualRow.key}
              data-index={index}
              data-thread-item=""
              ref={virtualizer.measureElement}
              style={style}
            >
              {labels ? <DayDividerBand label={labels.label} dateLabel={labels.dateLabel} /> : null}
              <ThreadListItem
                thread={row}
                index={sparseIndex}
                threadMembership={threadMemberIdsByThreadId[getThreadRowId(row)]}
                {...rowProps}
              />
            </div>
          );
        })}
      </div>
    </>
  );
});
